import React, { useState, useEffect } from 'react';
import { 
  AppBar, Toolbar, Typography, Drawer, Box, List, ListItem, 
  ListItemIcon, ListItemText, IconButton, Divider, Avatar,
  Menu, MenuItem, Badge, useTheme, alpha, Collapse
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import SchoolIcon from '@mui/icons-material/School';
import PaymentIcon from '@mui/icons-material/Payment';
import GradingIcon from '@mui/icons-material/Grading';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LogoutIcon from '@mui/icons-material/Logout';
import ChatBot from './ChatBot';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import ArchiveIcon from '@mui/icons-material/Archive';
import BackupIcon from '@mui/icons-material/Backup';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ClassIcon from '@mui/icons-material/Class';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TableChartIcon from '@mui/icons-material/TableChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BadgeIcon from '@mui/icons-material/Badge';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CategoryIcon from '@mui/icons-material/Category';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import SecurityIcon from '@mui/icons-material/Security';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WorkIcon from '@mui/icons-material/Work';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import NotificationBell from './NotificationBell';
import MessageIcon from '@mui/icons-material/Message';
import CampaignIcon from '@mui/icons-material/Campaign';
const logoPath = '/images/logo_sup2i.png';
const drawerWidth = 280;

function Layout({ children, user: propUser, onLogout, socket }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [user, setUser] = useState(propUser || null);
  const [openParametres, setOpenParametres] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { isAdmin, isDirecteur, isEnseignant, isEtudiant, isConsultant, isScolarite, isResponsableScolarite, isChefFiliere, isDirectionFinanciere, isDirectionGenerale, isCoordinateurPedagogique } = usePermissions();

  // Mettre à jour user si propUser change
  useEffect(() => {
    if (propUser) {
      setUser(propUser);
    }
  }, [propUser]);

  // Récupérer l'utilisateur depuis localStorage si pas dans props
  useEffect(() => {
    if (!user) {
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          setUser(JSON.parse(userData));
        } catch (e) {
          console.error('Erreur parsing user:', e);
        }
      }
    }
  }, []);

  // Vérifier si une route de paramètres est active
  const isParametresActive = location.pathname.startsWith('/parametres');

  // Ouvrir le sous-menu si on est dans les paramètres
  useEffect(() => {
    if (isParametresActive) {
      setOpenParametres(true);
    }
  }, [isParametresActive]);

  console.log('👤 [Layout] User:', user);
  console.log('🔑 [Layout] isAdmin:', isAdmin);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const handleNotifOpen = (event) => setNotifAnchorEl(event.currentTarget);
  const handleNotifClose = () => setNotifAnchorEl(null);

  // ============ FONCTION DE DÉCONNEXION ============
  const handleLogout = () => {
    console.log('🚪 [Layout] Clic sur Déconnexion');
    
    if (onLogout && typeof onLogout === 'function') {
      onLogout();
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    
    navigate('/login');
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  // ========== MENU SELON LE RÔLE ==========
  const getMenuItems = () => {
    const baseMenu = [
      { text: 'Tableau de Bord', icon: <DashboardIcon />, path: '/dashboard', color: '#3b82f6' },
    ];

    // Étudiants - accessible à ADMIN, DIRECTEUR, SCOLARITE, CONSULTANT (+ DIRECTION_GENERALE en lecture)
    if (isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isConsultant || isDirectionGenerale) {
      baseMenu.push({ text: 'Étudiants', icon: <PeopleIcon />, path: '/etudiants', color: '#10b981' });
      baseMenu.push({ text: 'Cartes Étudiants', icon: <CreditCardIcon />, path: '/cartes-etudiants', color: '#06b6d4' });
    }
    // Liste des étudiants en lecture seule pour la direction financière.
    if (isDirectionFinanciere) {
      baseMenu.push({ text: 'Étudiants', icon: <PeopleIcon />, path: '/etudiants', color: '#10b981' });
    }
    // Liste des étudiants en lecture seule pour le coordinateur pédagogique
    // (pas d'accès aux cartes étudiants).
    if (isCoordinateurPedagogique) {
      baseMenu.push({ text: 'Étudiants', icon: <PeopleIcon />, path: '/etudiants', color: '#10b981' });
    }

    // Enseignants - accessible à ADMIN, DIRECTEUR, SCOLARITE, CONSULTANT (+ DIRECTION_FINANCIERE, DIRECTION_GENERALE en lecture)
    if (isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isConsultant || isDirectionFinanciere || isDirectionGenerale) {
      baseMenu.push({ text: 'Enseignants', icon: <BadgeIcon />, path: '/enseignants', color: '#8b5cf6' });
    }

    // Inscriptions - masquée pour SCOLARITE (ADMIN, DIRECTEUR, CONSULTANT, DIRECTION_FINANCIERE, DIRECTION_GENERALE)
    if (isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isConsultant || isDirectionFinanciere || isDirectionGenerale) {
      baseMenu.push({ text: 'Inscriptions', icon: <MenuBookIcon />, path: '/inscriptions', color: '#f59e0b' });
    }

    // Paiements - masquée pour SCOLARITE (ADMIN, DIRECTEUR, CONSULTANT, DIRECTION_FINANCIERE, DIRECTION_GENERALE en lecture)
    if (isAdmin || isDirecteur || isConsultant || isDirectionFinanciere || isDirectionGenerale) {
      baseMenu.push({ text: 'Paiements', icon: <PaymentIcon />, path: '/paiements', color: '#8b5cf6' });
    }

    // Notes - accessible à ADMIN, DIRECTEUR, CHEF_FILIERE, ENSEIGNANT, DIRECTION_GENERALE (+ COORDINATEUR_PEDAGOGIQUE en lecture) - masqué pour ETUDIANT
if (isAdmin || isDirecteur || isChefFiliere || isEnseignant || isScolarite || isResponsableScolarite || isDirectionGenerale || isCoordinateurPedagogique) {
      baseMenu.push({ text: '📊 Notes', icon: <GradingIcon />, path: '/notes', color: '#ef4444' });
    }

    // Présences - accessible à ADMIN, DIRECTEUR, DIRECTION_GENERALE (lecture)
    if (isAdmin || isDirecteur || isDirectionGenerale) {
      baseMenu.push({ text: 'Présences', icon: <CheckCircleIcon />, path: '/presences', color: '#10b981' });
    }

    // Faire l'appel - accessible à ADMIN, DIRECTEUR, ENSEIGNANT
    if (isAdmin || isDirecteur || isEnseignant) {
      baseMenu.push({ text: '✅ Faire l\'appel', icon: <CheckCircleIcon />, path: '/faire-appel', color: '#10b981' });
    }

    // Consultation des appels effectués - la SCOLARITE consulte et imprime les appels
    // deja faits par les enseignants, sans pouvoir les modifier.
    if (isScolarite || isResponsableScolarite) {
      baseMenu.push({ text: '🔎 Consulter l\'appel', icon: <CheckCircleIcon />, path: '/faire-appel', color: '#10b981' });
    }

    // Passation - accessible à ADMIN, DIRECTEUR
    if (isAdmin || isDirecteur) {
      baseMenu.push({ text: 'Passation', icon: <TrendingUpIcon />, path: '/passation', color: '#06b6d4' });
    }

    // Emploi du Temps - accessible à ADMIN, DIRECTEUR, ENSEIGNANT, ETUDIANT, CHEF_FILIERE, DIRECTION_GENERALE (+ COORDINATEUR_PEDAGOGIQUE en lecture)
    if (isAdmin || isDirecteur || isEnseignant || isEtudiant || isChefFiliere || isScolarite || isResponsableScolarite || isDirectionGenerale || isCoordinateurPedagogique) {
      baseMenu.push({ text: 'Emploi du Temps', icon: <ScheduleIcon />, path: '/emploi-temps', color: '#06b6d4' });
    }

    // Emploi du Temps (Direction) - consultation & impression par groupe/prof - ADMIN, DIRECTEUR, SCOLARITE, CHEF_FILIERE, DIRECTION_GENERALE (+ COORDINATEUR_PEDAGOGIQUE en lecture)
    if (isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isChefFiliere || isDirectionGenerale || isCoordinateurPedagogique) {
      baseMenu.push({ text: 'Emploi (Direction)', icon: <TableChartIcon />, path: '/emploi-direction', color: '#0ea5e9' });
    }

    // Suivi des seances realisees / non realisees, base du paiement des enseignants
    if (isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isEnseignant || isChefFiliere || isDirectionFinanciere || isDirectionGenerale || isCoordinateurPedagogique) {
      baseMenu.push({ text: 'Suivi des seances', icon: <CheckCircleIcon />, path: '/suivi-seances', color: '#f59e0b' });
    }

    // Suivi des absences des etudiants - accessible à ADMIN, DIRECTEUR, SCOLARITE, CHEF_FILIERE, DIRECTION_GENERALE (+ COORDINATEUR_PEDAGOGIQUE en lecture)
    if (isAdmin || isDirecteur || isScolarite || isResponsableScolarite || isChefFiliere || isDirectionGenerale || isCoordinateurPedagogique) {
      baseMenu.push({ text: 'Suivi des absences', icon: <EventBusyIcon />, path: '/suivi-absences', color: '#ef4444' });
    }

    // ========== STAGES - accessible à ADMIN, DIRECTEUR, CHEF_FILIERE, DIRECTION_GENERALE (lecture) - masqué pour ETUDIANT ==========
    if (isAdmin || isDirecteur || isChefFiliere || isDirectionGenerale) {
      baseMenu.push({ text: '📋 Stages', icon: <WorkIcon />, path: '/stages', color: '#8b5cf6' });
    }

    // Espace Étudiant - accessible uniquement à ETUDIANT
    if (isEtudiant) {
      baseMenu.push({ text: '👨‍🎓 Mon Espace', icon: <AccountCircleIcon />, path: '/etudiant/dashboard', color: '#10b981' });
    }
if (isAdmin || isDirecteur || isChefFiliere || isEnseignant || isEtudiant || isDirectionGenerale) {
  baseMenu.push({ 
    text: '💬 Messages', 
    icon: <MessageIcon />, 
    path: '/messages', 
    color: '#8b5cf6' 
  });
}
    // Annonces - masquées pour l'enseignant (espace personnel) et pour le
    // coordinateur pédagogique, dont le périmètre est la consultation.
    if (!isEnseignant && !isCoordinateurPedagogique) {
      baseMenu.push({
        text: '📢 Annonces',
        icon: <CampaignIcon />,
        path: '/annonces',
        color: '#f59e0b'
      });
    }
    return baseMenu;
  };

  const menuItems = getMenuItems();

  // Menus Paramètres (Admin uniquement)
  const parametresItems = [
    { text: '📅 Années', icon: <CalendarTodayIcon />, path: '/parametres/annees', color: '#ef4444' },
    { text: '🎓 Filières', icon: <SchoolIcon />, path: '/parametres/filieres', color: '#3b82f6' },
    { text: '📚 Niveaux', icon: <MenuBookIcon />, path: '/parametres/niveaux', color: '#8b5cf6' },
    { text: '👥 Classes', icon: <ClassIcon />, path: '/parametres/classes', color: '#6366f1' },
    { text: '🏫 Salles', icon: <MeetingRoomIcon />, path: '/parametres/salles', color: '#0ea5e9' },
    { text: '📋 Périodes', icon: <ScheduleIcon />, path: '/parametres/periodes', color: '#f59e0b' },
    { text: '📦 Modules', icon: <CategoryIcon />, path: '/parametres/modules', color: '#10b981' },
    { text: '📖 Matières', icon: <LibraryBooksIcon />, path: '/parametres/matieres', color: '#06b6d4' },
    { text: '👥 Utilisateurs', icon: <PeopleIcon />, path: '/parametres/users', color: '#8b5cf6' },
    { text: '🛡️ Rôles', icon: <SecurityIcon />, path: '/parametres/roles', color: '#8b5cf6' },
    { text: '📋 Journal d\'audit', icon: <HistoryIcon />, path: '/parametres/audit-logs', color: '#8b5cf6' },
    { text: '📦 Archivage', icon: <ArchiveIcon />, path: '/parametres/archive', color: '#f97316' },
    { text: '💾 Sauvegarde', icon: <BackupIcon />, path: '/parametres/backup', color: '#0ea5e9' },
    { text: '👤 Personnel', icon: <PeopleIcon />, path: '/parametres/personnel', color: '#14b8a6' },
    { text: '🔔 Notifications', icon: <NotificationsIcon />, path: '/parametres/notifications', color: '#8b5cf6' },
  ];

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <Box sx={{ 
        p: 3, 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        borderBottom: '1px solid',
        borderColor: 'rgba(0,0,0,0.08)',
        pt: 4,
        pb: 3
      }}>
        <Box sx={{
          width: 120,
          height: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'white',
          borderRadius: '20px',
          padding: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
        }}>
          <img 
            src={logoPath} 
            alt="SUP2I Logo" 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain'
            }} 
          />
        </Box>
      </Box>

      {/* Menu utilisateur */}
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'rgba(0,0,0,0.08)', mx: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ 
            width: 48, 
            height: 48, 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 12px rgba(102,126,234,0.3)'
          }}>
            {user?.nom ? user.nom.charAt(0).toUpperCase() : 'A'}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1f2937' }}>
              {user?.nom || 'Utilisateur'} {user?.prenom || ''}
            </Typography>
            <Typography variant="caption" sx={{ 
              color: isAdmin ? '#10b981' : isDirecteur ? '#6d28d9' : isScolarite ? '#0891b2' : isResponsableScolarite ? '#06b6d4' : isChefFiliere ? '#0f766e' : isDirectionFinanciere ? '#b45309' : isDirectionGenerale ? '#6366f1' : isCoordinateurPedagogique ? '#0d9488' : isEnseignant ? '#3b82f6' : isEtudiant ? '#8b5cf6' : '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}>
              {isAdmin && '👑 Administrateur'}
              {isEnseignant && '👨‍🏫 Enseignant'}
              {isEtudiant && '👨‍🎓 Étudiant'}
              {isDirecteur && '🎯 Directeur'}
              {isScolarite && '📚 Scolarité'}
            {isResponsableScolarite && '📑 Responsable Scolarité'}
              {isChefFiliere && '🧭 Chef de filière'}
              {isDirectionFinanciere && '💳 Direction financière'}
              {isDirectionGenerale && '🏛️ Direction générale'}
              {isCoordinateurPedagogique && '🎓 Coordinateur pédagogique'}
              {isConsultant && '👁️ Consultant'}
              {!isAdmin && !isDirecteur && !isScolarite && !isResponsableScolarite && !isChefFiliere && !isDirectionFinanciere && !isDirectionGenerale && !isCoordinateurPedagogique && !isEnseignant && !isEtudiant && !isConsultant && 'Utilisateur'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, px: 2, py: 2 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem 
              button 
              key={item.text} 
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
              sx={{
                borderRadius: '12px',
                mb: 1,
                py: 1.2,
                transition: 'all 0.3s',
                background: isActive ? alpha(item.color, 0.1) : 'transparent',
                '&:hover': {
                  background: alpha(item.color, 0.08),
                  transform: 'translateX(4px)'
                }
              }}
            >
              <ListItemIcon sx={{ 
                color: isActive ? item.color : '#9ca3af',
                minWidth: 42
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                sx={{
                  '& .MuiTypography-root': {
                    fontWeight: isActive ? 600 : 400,
                    fontSize: '0.9rem',
                    color: isActive ? item.color : '#4b5563'
                  }
                }}
              />
              {isActive && (
                <Box sx={{
                  width: 4,
                  height: 28,
                  background: item.color,
                  borderRadius: '4px'
                }} />
              )}
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* Menu Paramètres avec sous-menus (Admin uniquement) */}
      {isAdmin && (
        <List sx={{ px: 2, py: 1 }}>
          <ListItem 
            button 
            onClick={() => setOpenParametres(!openParametres)}
            sx={{
              borderRadius: '12px',
              py: 1.2,
              background: isParametresActive ? alpha('#8b5cf6', 0.1) : 'transparent',
              '&:hover': {
                background: alpha('#8b5cf6', 0.08)
              }
            }}
          >
            <ListItemIcon sx={{ 
              color: isParametresActive ? '#8b5cf6' : '#9ca3af',
              minWidth: 42
            }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Paramètres" 
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: isParametresActive ? 600 : 400,
                  fontSize: '0.9rem',
                  color: isParametresActive ? '#8b5cf6' : '#4b5563'
                }
              }}
            />
            {openParametres ? <ExpandLessIcon sx={{ color: '#9ca3af' }} /> : <ExpandMoreIcon sx={{ color: '#9ca3af' }} />}
          </ListItem>
          
          <Collapse in={openParametres} timeout="auto" unmountOnExit>
            <Box sx={{ pl: 2 }}>
              {parametresItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <ListItem 
                    button 
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setMobileOpen(false);
                    }}
                    sx={{
                      borderRadius: '10px',
                      py: 0.8,
                      mb: 0.5,
                      background: isActive ? alpha(item.color, 0.1) : 'transparent',
                      '&:hover': {
                        background: alpha(item.color, 0.08)
                      }
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: isActive ? item.color : '#9ca3af',
                      minWidth: 36
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text} 
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? item.color : '#4b5563'
                      }}
                    />
                    {isActive && (
                      <Box sx={{
                        width: 3,
                        height: 20,
                        background: item.color,
                        borderRadius: '2px'
                      }} />
                    )}
                  </ListItem>
                );
              })}
            </Box>
          </Collapse>
        </List>
      )}

      <Divider />

      {/* Menu bas avec déconnexion */}
      <List sx={{ px: 2, py: 2 }}>
        <ListItem 
          button 
          onClick={handleLogout} 
          sx={{ 
            borderRadius: '12px', 
            py: 1.2,
            '&:hover': {
              background: 'rgba(239, 68, 68, 0.1)'
            }
          }}
        >
          <ListItemIcon sx={{ color: '#ef4444', minWidth: 42 }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Déconnexion" 
            sx={{ 
              '& .MuiTypography-root': { 
                fontSize: '0.9rem', 
                color: '#ef4444',
                fontWeight: 500
              } 
            }} 
          />
        </ListItem>
      </List>

      {/* Version */}
      <Box sx={{ p: 2, textAlign: 'center', borderTop: '1px solid', borderColor: 'rgba(0,0,0,0.08)' }}>
        <Typography variant="caption" sx={{ color: '#9ca3af' }}>
          Version 2.0.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="fixed" sx={{
        zIndex: theme.zIndex.drawer + 1,
        background: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        borderBottom: '1px solid',
        borderColor: 'rgba(0,0,0,0.05)'
      }}>
        <Toolbar sx={{ minHeight: '64px !important', px: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ 
                display: { sm: 'none' },
                color: '#6b7280'
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ 
              fontWeight: 600,
              color: '#1f2937',
              fontSize: { xs: '0.9rem', sm: '1.1rem' }
            }}>
              Gestion Scolarité SUP2I
            </Typography>
            {isConsultant && (
              <Box sx={{ ml: 2, px: 1.5, py: 0.5, background: '#fef3c7', borderRadius: '20px' }}>
                <Typography variant="caption" sx={{ color: '#92400e' }}>Mode lecture seule</Typography>
              </Box>
            )}
            {isEtudiant && (
              <Box sx={{ ml: 2, px: 1.5, py: 0.5, background: '#dbeafe', borderRadius: '20px' }}>
                <Typography variant="caption" sx={{ color: '#1e40af' }}>👨‍🎓 Espace étudiant</Typography>
              </Box>
            )}
            {isDirecteur && (
              <Box sx={{ ml: 2, px: 1.5, py: 0.5, background: '#ede9fe', borderRadius: '20px' }}>
                <Typography variant="caption" sx={{ color: '#6d28d9' }}>🎯 Directeur</Typography>
              </Box>
            )}
            {isScolarite && (
              <Box sx={{ ml: 2, px: 1.5, py: 0.5, background: '#cffafe', borderRadius: '20px' }}>
                <Typography variant="caption" sx={{ color: '#0891b2' }}>📚 Scolarité</Typography>
              </Box>
            )}
            {isChefFiliere && (
              <Box sx={{ ml: 2, px: 1.5, py: 0.5, background: '#ccfbf1', borderRadius: '20px' }}>
                <Typography variant="caption" sx={{ color: '#0f766e' }}>🧭 Chef de filière</Typography>
              </Box>
            )}
            {isDirectionFinanciere && (
              <Box sx={{ ml: 2, px: 1.5, py: 0.5, background: '#fef3c7', borderRadius: '20px' }}>
                <Typography variant="caption" sx={{ color: '#b45309' }}>💳 Direction financière</Typography>
              </Box>
            )}
            {isDirectionGenerale && (
              <Box sx={{ ml: 2, px: 1.5, py: 0.5, background: '#e0e7ff', borderRadius: '20px' }}>
                <Typography variant="caption" sx={{ color: '#4338ca' }}>🏛️ Direction générale — lecture seule</Typography>
              </Box>
            )}
            {isCoordinateurPedagogique && (
              <Box sx={{ ml: 2, px: 1.5, py: 0.5, background: '#ccfbf1', borderRadius: '20px' }}>
                <Typography variant="caption" sx={{ color: '#0d9488' }}>🎓 Coordinateur pédagogique — lecture seule</Typography>
              </Box>
            )}
            {isEnseignant && (
              <Box sx={{ ml: 2, px: 1.5, py: 0.5, background: '#dcfce7', borderRadius: '20px' }}>
                <Typography variant="caption" sx={{ color: '#166534' }}>👨‍🏫 Espace enseignant</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Notification Bell */}
            <NotificationBell socket={socket} />

            {/* User menu */}
            <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
              <Avatar sx={{ 
                width: 38, 
                height: 38, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                cursor: 'pointer'
              }}>
                {user?.nom ? user.nom.charAt(0).toUpperCase() : 'A'}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 220,
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
          }
        }}
      >
        <MenuItem onClick={() => { navigate('/profile'); handleMenuClose(); }}>
          <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Mon profil</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { navigate('/parametres/notifications'); handleMenuClose(); }}>
          <ListItemIcon><NotificationsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Notifications</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { navigate('/settings'); handleMenuClose(); }}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Paramètres</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: '#ef4444' }}>
          <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: '#ef4444' }} /></ListItemIcon>
          <ListItemText>Déconnexion</ListItemText>
        </MenuItem>
      </Menu>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              background: 'white',
              borderRight: 'none',
              top: '56px',
              height: 'calc(100% - 56px)'
            }
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              background: 'white',
              borderRight: '1px solid',
              borderColor: 'rgba(0,0,0,0.05)',
              boxShadow: 'none',
              top: '64px',
              height: 'calc(100% - 64px)'
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 25%, #f3e8ff 100%)',
          minHeight: '100vh',
          mt: '64px'
        }}
      >
        {children}
      </Box>
      <ChatBot />
    </Box>
  );
}

export default Layout;

